import * as React from "react";
import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Star, ShoppingCart, Heart, Share2, Truck, Shield, Plus, Minus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { supabase } from "@/integrations/supabase/client";
import { useCart } from "@/hooks/useCartSync";
import { useCurrency } from "@/hooks/useCurrency";
import { useProductDeal } from "@/hooks/useProductDeal";
import { useLanguage } from "@/hooks/useLanguage";
import { useSanitizedHTML } from "@/hooks/useSanitizedHTML";
import { formatPriceWithCurrency } from "@/utils/currency";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import ReviewSystem from "@/components/ReviewSystem";
import { DealBadge } from "@/components/DealBadge";
import { ProductImageGallery } from "@/components/products/ProductImageGallery";
import { ProductRating } from "@/components/ProductRating";

interface Product {
  id: string;
  title: string;
  title_translations?: Record<string, string>;
  subtitle?: string | null;
  subtitle_translations?: Record<string, string>;
  colors?: Array<{name: string, hex: string, stock: number}>;
  sizes?: Array<{size: string, stock: number}>;
  price_cents: number;
  currency: string;
  images: string[] | null;
  vendor_id: string;
  stock: number;
  active: boolean;
  description: string | null;
  description_translations?: Record<string, string>;
  category: string | null;
  brand: string | null;
  model: string | null;
  created_at: string;
}

const ProductDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedColor, setSelectedColor] = useState<{name: string, hex: string, stock: number} | null>(null);
  const [selectedSize, setSelectedSize] = useState<{size: string, stock: number} | null>(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [relatedProducts, setRelatedProducts] = useState<Product[]>([]);
  const [reviewStats, setReviewStats] = useState({ averageRating: 0, totalReviews: 0 });
  const [isFavorite, setIsFavorite] = useState(false);
  const { addItem, setIsOpen } = useCart();
  const { displayCurrency, convertPrice } = useCurrency();
  const { deal, calculateDiscountedPrice } = useProductDeal(product?.id || '');
  const { language, t } = useLanguage();

  // ✅ Helper function DÉCLARÉE AVANT utilisation
  const getTranslatedText = useCallback((translations: Record<string, string> | undefined, fallback: string): string => {
    if (!translations) return fallback;
    
    const targetLang = language === 'en' ? 'en' : 'fr';
    
    if (translations[targetLang]) return translations[targetLang];
    if (translations.fr) return translations.fr;
    if (translations.en) return translations.en;
    
    return fallback;
  }, [language]);
  // Helper function pour formatter les catégories
  const formatCategoryLabel = (slug: string): string => {
    const categoryLabels: Record<string, string> = {
      'mode-beaute': 'Mode & Beauté',
      'maison-jardin': 'Maison & Jardin',
      'electronique': 'Électronique',
      'sante-bien-etre': 'Santé & Bien-être',
      'livres-culture': 'Livres & Culture',
      'auto-moto': 'Auto & Moto',
      'sport-loisirs': 'Sport & Loisirs',
      'enfants-bebes': 'Enfants & Bébés',
      'jeux-jouets': 'Jeux & Jouets',
      'high-tech': 'High-Tech',
      'alimentation': 'Alimentation',
      'services': 'Services',
    };
    
    return categoryLabels[slug] || slug.split('-').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };
  // ✅ useMemo avec dépendances PRIMITIVES (pas l'objet product complet)
  const translatedDescription = React.useMemo(() => {
    if (!product) return "";
    return getTranslatedText(
      product.description_translations, 
      product.description || "Aucune description disponible pour ce produit."
    );
  }, [product?.id, product?.description, product?.description_translations, getTranslatedText]);

  const sanitizedDescription = useSanitizedHTML(translatedDescription);

  const discountedPrice = product && deal 
    ? calculateDiscountedPrice(product.price_cents) 
    : null;

  const fetchProduct = useCallback(async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('products')
        .select(`
          *,
          vendor:profiles!vendor_id(vendor_status, system_role)
        `)
        .eq('id', id)
        .eq('active', true)
        .single();

      if (error) throw error;
      
      // Verify that the vendor is approved OR is an admin
      const isAdmin = data.vendor?.system_role === 'admin';
      const isApproved = data.vendor?.vendor_status === 'approved';
      
      if (!isAdmin && !isApproved) {
        toast.error(t('product.notFound'));
        navigate('/');
        return;
      }
      
      setProduct(data as any);
    } catch (error) {
      console.error('Error fetching product:', error);
      toast.error(t('product.notFound'));
      navigate('/');
    } finally {
      setLoading(false);
    }
  }, [id, navigate, t]);

  const fetchReviewStats = useCallback(async () => {
    if (!product?.id) return;

    try {
      // Get average rating
      const { data: avgData, error: avgError } = await supabase
        .rpc('get_product_average_rating', { product_uuid: product.id });
      
      if (avgError) throw avgError;

      // Get review count
      const { data: countData, error: countError } = await supabase
        .rpc('get_product_review_count', { product_uuid: product.id });
      
      if (countError) throw countError;

      setReviewStats({
        averageRating: Number(avgData) || 0,
        totalReviews: countData || 0
      });
    } catch (error) {
      console.error('Error fetching review stats:', error);
    }
  }, [product?.id]);

  const fetchRelatedProducts = useCallback(async () => {
    if (!product?.id || !product?.category) return;
    
    try {
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .eq('active', true)
        .eq('category', product.category)
        .neq('id', product.id)
        .limit(4);

      if (error) throw error;
      setRelatedProducts((data || []) as any);
    } catch (error) {
      console.error('Error fetching related products:', error);
    }
  }, [product?.id, product?.category]);

  const checkIfFavorite = useCallback(async () => {
    if (!product?.id) return;

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('favorites' as any)
        .select('id')
        .eq('user_id', user.id)
        .eq('product_id', product.id)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') throw error;
      setIsFavorite(!!data);
    } catch (error) {
      console.error('Error checking favorite status:', error);
    }
  }, [product?.id]);

  useEffect(() => {
    if (id) {
      fetchProduct();
    }
  }, [id, fetchProduct]);

  useEffect(() => {
    if (product?.id) {
      fetchRelatedProducts();
      fetchReviewStats();
      checkIfFavorite();
    }
  }, [product?.id, fetchRelatedProducts, fetchReviewStats, checkIfFavorite]);

  const formatPrice = (cents: number, currency: string) => {
    return `${(cents / 100).toFixed(2)} ${currency}`;
  };

  const handleAddToCart = () => {
    if (!product) return;

    // Vérifier qu'une couleur est sélectionnée si le produit a des couleurs
    if (product.colors && product.colors.length > 0 && !selectedColor) {
      toast.error(t('product.selectColorFirst') || 'Veuillez sélectionner une couleur');
      return;
    }

    // Vérifier qu'une taille est sélectionnée si le produit a des tailles
    if (product.sizes && product.sizes.length > 0 && !selectedSize) {
      toast.error('Veuillez sélectionner une taille');
      return;
    }

    // Déterminer le stock disponible
    let availableStock = product.stock;
    if (selectedColor && selectedSize) {
      availableStock = Math.min(selectedColor.stock, selectedSize.stock);
    } else if (selectedColor) {
      availableStock = selectedColor.stock;
    } else if (selectedSize) {
      availableStock = selectedSize.stock;
    }

    // Vérifier le stock disponible
    if (availableStock === 0) {
      toast.error('Cette variante est en rupture de stock');
      return;
    }
    if (quantity > availableStock) {
      toast.error(`Stock insuffisant (${availableStock} disponible${availableStock > 1 ? 's' : ''})`);
      return;
    }

    const finalPrice = deal && discountedPrice !== null ? discountedPrice : product.price_cents;

    // Construire le titre avec couleur et taille
    let itemTitle = getTranslatedText(product.title_translations, product.title);
    if (selectedColor) itemTitle += ` - ${selectedColor.name}`;
    if (selectedSize) itemTitle += ` - Taille ${selectedSize.size}`;

    addItem({
      id: product.id,
      title: itemTitle,
      price_cents: finalPrice,
      currency: product.currency,
      images: product.images || [],
      vendor_id: product.vendor_id
    }, quantity);
  };

  const handleBuyNow = async () => {
    if (!product) return;

    // Vérifier que l'utilisateur est connecté
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      toast.error('Vous devez être connecté pour acheter');
      navigate('/login');
      return;
    }

    // Vérifier qu'une couleur est sélectionnée si le produit a des couleurs
    if (product.colors && product.colors.length > 0 && !selectedColor) {
      toast.error(t('product.selectColorFirst') || 'Veuillez sélectionner une couleur');
      return;
    }

    // Vérifier qu'une taille est sélectionnée si le produit a des tailles
    if (product.sizes && product.sizes.length > 0 && !selectedSize) {
      toast.error('Veuillez sélectionner une taille');
      return;
    }

    // Déterminer le stock disponible
    let availableStock = product.stock;
    if (selectedColor && selectedSize) {
      availableStock = Math.min(selectedColor.stock, selectedSize.stock);
    } else if (selectedColor) {
      availableStock = selectedColor.stock;
    } else if (selectedSize) {
      availableStock = selectedSize.stock;
    }

    // Vérifier le stock disponible
    if (availableStock === 0) {
      toast.error('Cette variante est en rupture de stock');
      return;
    }
    if (quantity > availableStock) {
      toast.error(`Stock insuffisant (${availableStock} disponible${availableStock > 1 ? 's' : ''})`);
      return;
    }

    const finalPrice = deal && discountedPrice !== null ? discountedPrice : product.price_cents;

    // Préparer les données de l'achat direct avec calcul de commission
    try {
      const { data: productData } = await supabase
        .from('products')
        .select(`
          id,
          category_id,
          categories:category_id (
            commission_rate
          )
        `)
        .eq('id', product.id)
        .single();

      const commissionRate = productData?.categories?.commission_rate ?? 0.15;
      const subtotal = finalPrice * quantity;
      const commissionAmount = Math.round(subtotal * commissionRate);
      const netAmount = subtotal - commissionAmount;

      const directPurchaseItem = {
        product_id: product.id,
        vendor_id: product.vendor_id,
        quantity: quantity,
        price_cents: finalPrice,
        currency: product.currency,
        subtotal_cents: subtotal,
        commission_rate: commissionRate,
        commission_cents: commissionAmount,
        net_cents: netAmount,
        // Informations supplémentaires pour l'affichage
        title: getTranslatedText(product.title_translations, product.title),
        color: selectedColor?.name,
        size: selectedSize?.size,
        images: product.images || []
      };

      // Stocker en sessionStorage pour le checkout (comme le panier)
      sessionStorage.setItem('directPurchase', JSON.stringify({
        item: directPurchaseItem,
        clientId: user.id
      }));

      // Naviguer vers le panier qui va détecter l'achat direct
      setIsOpen(true);
      
      toast.success('Prêt pour le paiement !');
    } catch (error: any) {
      console.error('[ProductDetail] Error preparing buy now:', error);
      toast.error('Erreur lors de la préparation de l\'achat');
    }
  };

  const handleQuantityChange = (delta: number) => {
    const newQuantity = Math.max(1, Math.min(quantity + delta, product?.stock || 1));
    setQuantity(newQuantity);
  };

  const handleToggleFavorite = async () => {
    if (!product) return;

    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        toast.error(t('auth.loginRequired'));
        navigate('/auth');
        return;
      }

      if (isFavorite) {
        // Remove from favorites
        const { error } = await supabase
          .from('favorites' as any)
          .delete()
          .eq('user_id', user.id)
          .eq('product_id', product.id);

        if (error) throw error;
        setIsFavorite(false);
        toast.success(t('product.removedFromFavorites'));
      } else {
        // Add to favorites
        const { error } = await supabase
          .from('favorites' as any)
          .insert({
            user_id: user.id,
            product_id: product.id
          });

        if (error) throw error;
        setIsFavorite(true);
        toast.success(t('product.addedToFavorites'));
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
      toast.error(t('product.favoriteError'));
    }
  };

  const handleShare = async () => {
    if (!product) return;

    const shareData = {
      title: getTranslatedText(product.title_translations, product.title),
      text: t('product.shareText'),
      url: window.location.href
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        // Fallback: copy to clipboard
        await navigator.clipboard.writeText(window.location.href);
        toast.success(t('product.linkCopied'));
      }
    } catch (error) {
      console.error('Error sharing:', error);
      // If user cancelled or error, copy to clipboard
      try {
        await navigator.clipboard.writeText(window.location.href);
        toast.success(t('product.linkCopied'));
      } catch (clipboardError) {
        toast.error(t('product.shareError'));
      }
    }
  };

  if (loading || !product) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="container py-8">
          <div className="animate-pulse">
            <div className="grid md:grid-cols-2 gap-8">
              <div className="aspect-square bg-muted rounded-lg"></div>
              <div className="space-y-4">
                <div className="h-8 bg-muted rounded w-3/4"></div>
                <div className="h-6 bg-muted rounded w-1/2"></div>
                <div className="h-4 bg-muted rounded w-full"></div>
                <div className="h-4 bg-muted rounded w-2/3"></div>
              </div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const productImages = product.images && product.images.length > 0 
    ? product.images 
    : ['/placeholder.svg'];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container py-8">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 mb-6">
          <Button variant="ghost" size="sm" onClick={() => navigate('/')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t('product.back')}
          </Button>
          <span className="text-muted-foreground">/</span>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => navigate(`/category/${encodeURIComponent(product.category || '')}`)}
          >
            {product.category && (t(`category.${product.category}`) !== `category.${product.category}` ? t(`category.${product.category}`) : product.category)}
          </Button>
          <span className="text-muted-foreground">/</span>
          <span className="font-medium truncate">{getTranslatedText(product.title_translations, product.title)}</span>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 mb-12">
          {/* Images Section */}
          <ProductImageGallery
            images={productImages}
            productTitle={getTranslatedText(product.title_translations, product.title)}
            initialIndex={selectedImageIndex}
          />

          {/* Product Info Section */}
          <div className="space-y-6">
            <div>
              <h1 className="text-3xl font-bold mb-2">{getTranslatedText(product.title_translations, product.title)}</h1>
              
              {/* Subtitle */}
              {product.subtitle && (
                <p className="text-lg text-muted-foreground mb-3">
                  {getTranslatedText(product.subtitle_translations, product.subtitle)}
                </p>
              )}
              
              {/* Brand */}
              {product.brand && (
                <div className="mb-3">
                  <span className="text-sm text-muted-foreground">{t('product.brand')} : </span>
                  <button
                    onClick={() => navigate(`/all-products?brand=${encodeURIComponent(product.brand!)}`)}
                    className="text-sm font-medium text-primary hover:underline cursor-pointer"
                  >
                    {product.brand}
                  </button>
                </div>
              )}
              
              <div className="mb-4">
                <ProductRating 
                  rating={reviewStats.averageRating || 0} 
                  reviewCount={reviewStats.totalReviews || 0}
                  size="md"
                />
              </div>
              
              {product.category && (
                <Badge variant="secondary" className="mb-4">
                  {formatCategoryLabel(product.category)}
                </Badge>
              )}
              
              {/* Color Selection */}
              {product.colors && product.colors.length > 0 && (
                <div className="space-y-3 border-t border-b py-4">
                  <div>
                    <span className="text-sm font-semibold">Couleur : </span>
                    <span className="text-sm">{selectedColor?.name || 'Choisir une couleur'}</span>
                  </div>
                  <div className="flex flex-wrap gap-3">
                    {product.colors.map((color, index) => (
                      <button
                        key={index}
                        type="button"
                        onClick={() => color.stock > 0 && setSelectedColor(color)}
                        disabled={color.stock === 0}
                        className={`group relative ${color.stock === 0 ? 'opacity-30 cursor-not-allowed' : 'cursor-pointer hover:opacity-80'}`}
                        title={`${color.name}${color.stock > 0 ? ` - ${color.stock} en stock` : ' - Rupture de stock'}`}
                      >
                        <div 
                          className={`w-14 h-14 rounded-md border-2 transition-all flex items-center justify-center ${
                            selectedColor?.hex === color.hex 
                              ? 'border-primary ring-2 ring-primary shadow-md scale-105' 
                              : 'border-gray-300 hover:border-gray-400 hover:shadow-sm'
                          }`}
                          style={{ backgroundColor: color.hex }}
                        >
                          {/* Checkmark pour couleur sélectionnée */}
                          {selectedColor?.hex === color.hex && (
                            <svg className="w-6 h-6 text-white drop-shadow-lg" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          )}
                        </div>
                        {/* Nom de la couleur sous le carré */}
                        <span className={`block text-xs text-center mt-1 ${selectedColor?.hex === color.hex ? 'font-semibold' : ''}`}>
                          {color.name}
                        </span>
                        {/* Croix rouge pour rupture de stock */}
                        {color.stock === 0 && (
                          <div className="absolute inset-0 flex items-center justify-center">
                            <div className="w-0.5 h-16 bg-red-500 rotate-45 shadow-sm" />
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                  {!selectedColor && product.colors.length > 0 && (
                    <p className="text-sm text-amber-600 font-medium">
                      ⚠️ Veuillez sélectionner une couleur
                    </p>
                  )}
                </div>
              )}

              {/* Size Selection */}
              {product.sizes && product.sizes.length > 0 && (
                <div className="space-y-3 border-b py-4">
                  <div>
                    <span className="text-sm font-semibold">Taille : </span>
                    <span className="text-sm">{selectedSize?.size || 'Choisir une taille'}</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {product.sizes.map((size, index) => (
                      <button
                        key={index}
                        type="button"
                        onClick={() => size.stock > 0 && setSelectedSize(size)}
                        disabled={size.stock === 0}
                        className={`px-4 py-2 border-2 rounded-md font-medium transition-all ${
                          size.stock === 0 
                            ? 'opacity-30 cursor-not-allowed bg-gray-100' 
                            : 'cursor-pointer hover:border-gray-400 hover:shadow-sm'
                        } ${
                          selectedSize?.size === size.size
                            ? 'border-primary ring-2 ring-primary bg-primary/5 text-primary scale-105 shadow-md'
                            : 'border-gray-300'
                        }`}
                        title={`Taille ${size.size}${size.stock > 0 ? ` - ${size.stock} en stock` : ' - Rupture de stock'}`}
                      >
                        {size.size}
                        {size.stock === 0 && (
                          <span className="ml-1 text-red-500">✕</span>
                        )}
                      </button>
                    ))}
                  </div>
                  {!selectedSize && product.sizes.length > 0 && (
                    <p className="text-sm text-amber-600 font-medium">
                      ⚠️ Veuillez sélectionner une taille
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Price Section with Deal */}
            <div className="space-y-3">
              {deal && (
                <div className="inline-flex flex-col gap-1">
                  <Badge className="bg-destructive text-destructive-foreground font-bold">
                    -{deal.discount_percentage}%
                  </Badge>
                  <Badge variant="secondary" className="text-xs">
                    {deal.badge_text}
                  </Badge>
                </div>
              )}
              
              <div className="flex items-baseline gap-3">
                {deal && discountedPrice !== null ? (
                  <>
                    <span className="text-destructive font-bold text-2xl">
                      -{deal.discount_percentage}%
                    </span>
                    <span className="text-4xl font-bold text-primary">
                      {formatPriceWithCurrency(discountedPrice, product.currency, displayCurrency, convertPrice)}
                    </span>
                  </>
                ) : (
                  <span className="text-4xl font-bold text-primary">
                    {formatPriceWithCurrency(product.price_cents, product.currency, displayCurrency, convertPrice)}
                  </span>
                )}
              </div>
              
              {deal && discountedPrice !== null && (
                <div className="text-sm text-muted-foreground">
                  <span>Prix habituel : </span>
                  <span className="line-through">
                    {formatPriceWithCurrency(product.price_cents, product.currency, displayCurrency, convertPrice)}
                  </span>
                </div>
              )}
            </div>

            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Truck className="h-5 w-5 text-green-600" />
                <span className="text-sm">{t('product.deliveryAvailable')}</span>
              </div>
              <div className="flex items-center gap-2">
                <Shield className="h-5 w-5 text-blue-600" />
                <span className="text-sm">{t('product.securePayment')}</span>
              </div>
            </div>

            <Separator />

            {/* Stock and Quantity */}
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                {(() => {
                  // Si le produit a des couleurs ou des tailles
                  const hasColors = product.colors && product.colors.length > 0;
                  const hasSizes = product.sizes && product.sizes.length > 0;
                  
                  if (hasColors || hasSizes) {
                    // Vérifier si couleur requise mais non sélectionnée
                    if (hasColors && !selectedColor) {
                      return (
                        <span className="text-sm font-medium text-amber-600">
                          ⚠️ Sélectionnez une couleur pour voir le stock
                        </span>
                      );
                    }
                    
                    // Vérifier si taille requise mais non sélectionnée
                    if (hasSizes && !selectedSize) {
                      return (
                        <span className="text-sm font-medium text-amber-600">
                          ⚠️ Sélectionnez une taille pour voir le stock
                        </span>
                      );
                    }
                    
                    // Déterminer le stock à afficher
                    let stockToDisplay = product.stock;
                    if (selectedColor && selectedSize) {
                      // Les deux sélectionnés : prendre le min pour être sûr
                      stockToDisplay = Math.min(selectedColor.stock, selectedSize.stock);
                    } else if (selectedColor) {
                      stockToDisplay = selectedColor.stock;
                    } else if (selectedSize) {
                      stockToDisplay = selectedSize.stock;
                    }
                    
                    return (
                      <span className={`text-sm font-medium ${stockToDisplay > 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {stockToDisplay > 0 
                          ? `${t('product.inStock')} (${stockToDisplay} ${t('product.available')})`
                          : 'Rupture de stock pour cette variante'}
                      </span>
                    );
                  }
                  
                  // Produit sans couleurs ni tailles
                  return (
                    <span className={`text-sm font-medium ${product.stock > 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {product.stock > 0 
                        ? `${t('product.inStock')} (${product.stock} ${t('product.available')})`
                        : t('product.outOfStock')}
                    </span>
                  );
                })()}
              </div>
              
              {(() => {
                // Déterminer le stock disponible en fonction des sélections
                const hasColors = product.colors && product.colors.length > 0;
                const hasSizes = product.sizes && product.sizes.length > 0;
                
                let hasStock = product.stock > 0;
                let maxStock = product.stock;
                
                if (hasColors || hasSizes) {
                  if (hasColors && !selectedColor) {
                    hasStock = false;
                  } else if (hasSizes && !selectedSize) {
                    hasStock = false;
                  } else {
                    // Calculer le stock disponible
                    if (selectedColor && selectedSize) {
                      maxStock = Math.min(selectedColor.stock, selectedSize.stock);
                    } else if (selectedColor) {
                      maxStock = selectedColor.stock;
                    } else if (selectedSize) {
                      maxStock = selectedSize.stock;
                    }
                    hasStock = maxStock > 0;
                  }
                }
                
                return hasStock && (
                <div className="flex items-center gap-4">
                  <span className="text-sm font-medium">{t('product.quantity')}:</span>
                  <div className="flex items-center border rounded-lg">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleQuantityChange(-1)}
                      disabled={quantity <= 1}
                    >
                      <Minus className="h-4 w-4" />
                    </Button>
                    <span className="px-4 py-2 min-w-[3rem] text-center">{quantity}</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleQuantityChange(1)}
                      disabled={quantity >= maxStock}
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              );})()}
            </div>

            {/* Action Buttons */}
            <div className="space-y-3">
              <Button
                size="lg"
                className="w-full bg-orange-600 hover:bg-orange-700"
                onClick={handleBuyNow}
                disabled={product.stock <= 0}
              >
                <ShoppingCart className="h-5 w-5 mr-2" />
                Acheter maintenant
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="w-full"
                onClick={handleAddToCart}
                disabled={product.stock <= 0}
              >
                <ShoppingCart className="h-5 w-5 mr-2" />
                {t('product.addToCart')}
              </Button>
              <div className="flex gap-3">
                <Button 
                  variant="outline" 
                  size="lg" 
                  className="flex-1"
                  onClick={handleToggleFavorite}
                >
                  <Heart className={`h-5 w-5 mr-2 ${isFavorite ? 'fill-current text-red-500' : ''}`} />
                  {t('product.favorites')}
                </Button>
                <Button 
                  variant="outline" 
                  size="lg" 
                  className="flex-1"
                  onClick={handleShare}
                >
                  <Share2 className="h-5 w-5 mr-2" />
                  {t('product.share')}
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Product Details Tabs */}
        <Tabs defaultValue="description" className="mb-12">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="description">{t('product.description')}</TabsTrigger>
            <TabsTrigger value="specifications">{t('product.specifications')}</TabsTrigger>
            <TabsTrigger value="reviews">{t('product.reviews_tab')}</TabsTrigger>
          </TabsList>
          
          <TabsContent value="description" className="mt-6">
            <Card>
              <CardContent className="p-6">
                <div 
                  className="prose prose-sm md:prose-base max-w-none
                    prose-headings:font-semibold prose-headings:text-foreground
                    prose-p:text-muted-foreground prose-p:leading-relaxed
                    prose-ul:text-muted-foreground prose-ol:text-muted-foreground
                    prose-li:marker:text-primary
                    prose-strong:text-foreground prose-strong:font-semibold
                    prose-a:text-primary prose-a:no-underline hover:prose-a:underline"
                  dangerouslySetInnerHTML={{ 
                    __html: sanitizedDescription
                  }}
                />
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="specifications" className="mt-6">
            <Card>
              <CardContent className="p-6">
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    {product.brand && (
                      <div>
                        <span className="font-medium">{t('product.brand')}:</span>
                        <button
                          onClick={() => navigate(`/all-products?brand=${encodeURIComponent(product.brand!)}`)}
                          className="ml-2 text-muted-foreground hover:text-primary hover:underline cursor-pointer"
                        >
                          {product.brand}
                        </button>
                      </div>
                    )}
                    {product.model && (
                      <div>
                        <span className="font-medium">{t('product.model')}:</span>
                        <span className="ml-2 text-muted-foreground">{product.model}</span>
                      </div>
                    )}
                    <div>
                      <span className="font-medium">{t('product.category')}:</span>
                      <span className="ml-2 text-muted-foreground">
                        {product.category ? (t(`category.${product.category}`) !== `category.${product.category}` ? t(`category.${product.category}`) : product.category) : t('product.notSpecified')}
                      </span>
                    </div>
                    <div>
                      <span className="font-medium">{t('product.stock')}:</span>
                      <span className="ml-2 text-muted-foreground">{product.stock} {t('product.units')}</span>
                    </div>
                    <div>
                      <span className="font-medium">{t('product.reference')}:</span>
                      <span className="ml-2 text-muted-foreground">{product.id.slice(0, 8)}</span>
                    </div>
                    <div>
                      <span className="font-medium">{t('product.dateAdded')}:</span>
                      <span className="ml-2 text-muted-foreground">
                        {new Date(product.created_at).toLocaleDateString(language === 'en' ? 'en-US' : 'fr-FR')}
                      </span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="reviews" className="mt-6">
            <ReviewSystem productId={product.id} />
          </TabsContent>
        </Tabs>

        {/* Related Products */}
        {relatedProducts.length > 0 && (
          <section>
            <h2 className="text-2xl font-bold mb-6">{t('product.relatedProducts')}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {relatedProducts.map((relatedProduct) => (
                <Card 
                  key={relatedProduct.id}
                  className="group cursor-pointer hover:shadow-elegant transition-all duration-300 hover:-translate-y-1"
                  onClick={() => navigate(`/product/${relatedProduct.id}`)}
                >
                  <div className="relative aspect-square overflow-hidden rounded-t-lg">
                    <img
                      src={relatedProduct.images?.[0] || '/placeholder.svg'}
                      alt={relatedProduct.title}
                      className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                    />
                  </div>
                  <CardContent className="p-4">
                    <h3 className="font-semibold mb-2 line-clamp-2 group-hover:text-primary transition-colors">
                      {getTranslatedText(relatedProduct.title_translations, relatedProduct.title)}
                    </h3>
                    <div className="text-lg font-bold text-primary">
                      {formatPriceWithCurrency(relatedProduct.price_cents, relatedProduct.currency, displayCurrency, convertPrice)}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default ProductDetail;